import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
    Menu, X, BookOpen, LogOut, User as UserIcon,
    ChevronDown, Check, Users, PlusCircle, Settings, Wallet,
    Globe, FileCode, Lock, Zap, Clock, Database, Shield,
    TrendingUp, GitBranch, Terminal, Network
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import TeamSettingsModal from '../Modals/TeamSettingsModal';
import NewServiceDropdown from '../Dashboard/NewServiceDropdown';
import NotificationCenter from './NotificationCenter';
import { CORE_SERVICE_TYPES } from '../../config/serviceTypes';

const Navbar = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showTeamDropdown, setShowTeamDropdown] = useState(false);
    const [showTeamSettings, setShowTeamSettings] = useState(false);
    const [showProductsDropdown, setShowProductsDropdown] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, teams, selectedTeam, setSelectedTeam } = useAuth();

    useEffect(() => {
        setMobileMenuOpen(false);
        setShowProductsDropdown(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') {
                setMobileMenuOpen(false);
                setShowProductsDropdown(false);
            }
        };
        document.addEventListener('keydown', closeOnEscape);
        return () => document.removeEventListener('keydown', closeOnEscape);
    }, []);

    const handleNavigate = (path, external = false) => {
        if (external) {
            window.open(path, '_blank', 'noopener,noreferrer');
        } else {
            navigate(path);
        }
        setMobileMenuOpen(false);
        setShowProductsDropdown(false);
    };
    const isPublicPage = !user;
    const isDashboard = location.pathname.startsWith('/dashboard');
    const isMarketplace = location.pathname.startsWith('/marketplace');

    const products = {
        deployment: [
            { icon: Globe, name: 'Web Service', desc: 'REST APIs, GraphQL, Full-stack apps', path: '/dashboard?create=web' },
            { icon: FileCode, name: 'Static Site', desc: 'React, Vue, Flutter Web, Nginx-served', path: '/dashboard?create=static' },
            { icon: Lock, name: 'Private Service', desc: 'Internal APIs, Microservices', path: '/dashboard?create=private' },
            { icon: Zap, name: 'Background Worker', desc: 'Email sending, Job processing', path: '/dashboard?create=worker' },
            { icon: Clock, name: 'Cron Job', desc: 'Reports, Backups, Scheduled scripts', path: '/dashboard?create=cron' },
            { icon: Database, name: 'Redis Datastore', desc: 'Persistent Redis for your services', path: '/dashboard?create=redis' }
        ],
        ai: [
            { icon: Database, name: 'Vitibase', desc: 'PostgreSQL, Auth, Storage, Realtime, REST APIs', path: 'https://app.vitibase.com/sign-up', external: true, badge: 'BaaS' }
        ],
        platform: [
            { icon: GitBranch, name: 'GitHub Source', desc: 'Repository and branch selection', path: '/docs/integrations/github' },
            { icon: Terminal, name: 'Docker Builds', desc: 'Framework and Docker build paths', path: '/docs/build-deploy/framework-detection' },
            { icon: Shield, name: 'Domains & TLS', desc: 'Custom domains and managed HTTPS', path: '/dashboard/domains' },
            { icon: TrendingUp, name: 'Compute Tiers', desc: 'Manual vertical scaling controls', path: '/docs/infrastructure/scaling' },
            { icon: Network, name: 'Private Networks', desc: 'Project service connectivity', path: '/networks' }
        ]
    };

    return (
        <>
            {isPublicPage && <a href="#main-content" className="df-skip-link">Skip to main content</a>}
            <nav className={`${location.pathname === "/" ? "rp-navbar " : ""}${location.pathname.startsWith("/ai-search") ? "ais-navbar " : ""}fixed top-0 left-0 right-0 z-[100] backdrop-blur-xl bg-root/70 border-b border-inner shadow-sm transition-all duration-300`}>
                <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        {/* Left Side: Logo & Team Context */}
                        <div className="flex items-center gap-4">
                            <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-3" aria-label="Replov home">
                                <img src="/logo192.png" alt="replov" className="w-12 h-12 rounded-xl shadow-lg" />
                                {/* Show Replov name on all screen sizes */}
                                <span className="text-2xl sm:text-3xl font-bold text-primary" style={{ fontFamily: 'DM Sans, sans-serif', letterSpacing: '-0.02em', fontWeight: 700 }}>
                                    <span style={{ color: '#fafafa' }}>repl</span>
                                    <span style={{ background: 'linear-gradient(135deg, #4ade80, #06b6d4, #818cf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>ov</span>
                                </span>
                            </Link>
                            {isMarketplace && (
                                <div className="hidden sm:flex items-center gap-2">
                                    <div className="h-6 w-px bg-inner"></div>
                                    <span className="text-sm font-bold text-indigo tracking-wide uppercase">Agent Marketplace</span>
                                </div>
                            )}

                            {/* Team Dropdown for Logged In Users */}
                            {user && !isMarketplace && (
                                <div className="relative pl-4 border-l border-inner hidden md:block">
                                    <button
                                        onClick={() => setShowTeamDropdown(!showTeamDropdown)}
                                        className="flex items-center gap-2 text-sm text-body hover:text-primary transition font-medium focus:outline-none bg-input px-3 py-1.5 rounded-lg border border-transparent hover:border-emphasis"
                                    >
                                        <span className="truncate max-w-[150px]">
                                            {selectedTeam?.name || 'Personal Workspace'}
                                        </span>
                                        <ChevronDown className="w-4 h-4" />
                                    </button>

                                    {showTeamDropdown && (
                                        <>
                                            <div className="fixed inset-0 z-40 bg-root/20 backdrop-blur-sm" onClick={() => setShowTeamDropdown(false)}></div>
                                            <div className="absolute top-12 left-0 w-64 bg-card border border-inner rounded-xl shadow-xl py-2 z-50">
                                                <div className="px-4 py-2 text-xs font-bold text-muted uppercase tracking-wider">
                                                    Your Teams
                                                </div>
                                                {teams.map(team => (
                                                    <button
                                                        key={team.id}
                                                        onClick={() => { setSelectedTeam(team); setShowTeamDropdown(false); }}
                                                        className={`w-full text-left px-4 py-2 flex items-center justify-between hover:bg-hover transition ${selectedTeam?.id === team.id ? 'bg-indigo/10 text-indigo' : 'text-body'}`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <Users className="w-4 h-4 opacity-70" />
                                                            <span className="font-medium text-sm">{team.name}</span>
                                                        </div>
                                                        {selectedTeam?.id === team.id && (
                                                            <Check className="w-4 h-4 text-indigo" />
                                                        )}
                                                    </button>
                                                ))}

                                                <div className="border-t border-inner mt-1 pt-1">
                                                    <button
                                                        onClick={() => { setShowTeamSettings(true); setShowTeamDropdown(false); }}
                                                        className="w-full text-left px-4 py-2 flex items-center gap-3 text-body hover:bg-hover hover:text-primary transition"
                                                    >
                                                        <Settings className="w-4 h-4 text-muted" />
                                                        <span className="font-medium text-sm">Team Settings</span>
                                                    </button>
                                                    <button
                                                        onClick={() => { setShowTeamDropdown(false); }}
                                                        className="w-full text-left px-4 py-2 flex items-center gap-3 text-indigo hover:bg-hover transition"
                                                    >
                                                        <PlusCircle className="w-4 h-4" />
                                                        <span className="font-semibold text-sm">Create New Team</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Desktop Menu */}
                        <div className="hidden md:flex items-center gap-4 font-medium">
                            {/* Products Dropdown - Always visible */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowProductsDropdown(!showProductsDropdown)}
                                    onMouseEnter={() => setShowProductsDropdown(true)}
                                    aria-expanded={showProductsDropdown}
                                    aria-controls="desktop-products-navigation"
                                    className="flex items-center gap-1 text-body hover:text-primary transition py-2"
                                >
                                    Products
                                    <ChevronDown className={`w-4 h-4 transition-transform ${showProductsDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {showProductsDropdown && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setShowProductsDropdown(false)}></div>
                                        <div
                                            id="desktop-products-navigation"
                                            className="absolute top-12 left-1/2 -translate-x-1/2 w-[720px] max-w-[95vw] bg-card border border-inner rounded-xl shadow-2xl py-6 px-6 z-50"
                                            onMouseLeave={() => setShowProductsDropdown(false)}
                                        >
                                            <div className="grid grid-cols-3 gap-6">
                                                {/* Deployment Services */}
                                                <div>
                                                    <div className="text-xs font-bold text-muted uppercase tracking-wider mb-3 px-2">
                                                        Deployment
                                                    </div>
                                                    <div className="space-y-1">
                                                        {products.deployment.map((product) => (
                                                            <Link
                                                                key={product.name}
                                                                to={product.path}
                                                                onClick={() => setShowProductsDropdown(false)}
                                                                className="w-full text-left px-2 py-2 rounded-lg hover:bg-hover transition group"
                                                            >
                                                                <div className="flex items-start gap-3">
                                                                    <product.icon className="w-5 h-5 text-indigo mt-0.5 flex-shrink-0" />
                                                                    <div>
                                                                        <div className="text-sm font-semibold text-primary group-hover:text-indigo transition">
                                                                            {product.name}
                                                                        </div>
                                                                        <div className="text-xs text-muted leading-tight">
                                                                            {product.desc}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Data & Backend */}
                                                <div>
                                                    <div className="text-xs font-bold text-muted uppercase tracking-wider mb-3 px-2">
                                                        Data & Backend
                                                    </div>
                                                    <div className="space-y-1">
                                                        {products.ai.map((product) => (
                                                            <a
                                                                key={product.name}
                                                                href={product.path}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={() => setShowProductsDropdown(false)}
                                                                className="w-full text-left px-2 py-2 rounded-lg hover:bg-hover transition group"
                                                            >
                                                                <div className="flex items-start gap-3">
                                                                    <product.icon className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                                                                    <div>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-sm font-semibold text-primary group-hover:text-emerald-400 transition">
                                                                                {product.name}
                                                                            </span>
                                                                            {product.badge && (
                                                                                <span className="bg-emerald-400/20 text-emerald-400 text-[10px] font-bold px-1.5 py-0.5 rounded">
                                                                                    {product.badge}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-xs text-muted leading-tight">
                                                                            {product.desc}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Platform Features */}
                                                <div>
                                                    <div className="text-xs font-bold text-muted uppercase tracking-wider mb-3 px-2">
                                                        Platform
                                                    </div>
                                                    <div className="space-y-1">
                                                        {products.platform.map((product) => (
                                                            <Link
                                                                key={product.name}
                                                                to={product.path}
                                                                onClick={() => setShowProductsDropdown(false)}
                                                                className="w-full text-left px-2 py-2 rounded-lg hover:bg-hover transition group"
                                                            >
                                                                <div className="flex items-start gap-3">
                                                                    <product.icon className="w-5 h-5 text-purple mt-0.5 flex-shrink-0" />
                                                                    <div>
                                                                        <div className="text-sm font-semibold text-primary group-hover:text-purple transition">
                                                                            {product.name}
                                                                        </div>
                                                                        <div className="text-xs text-muted leading-tight">
                                                                            {product.desc}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Bottom CTA */}
                                            <div className="mt-6 pt-4 border-t border-inner">
                                                <Link
                                                    to="/services"
                                                    onClick={() => setShowProductsDropdown(false)}
                                                    className="text-sm text-indigo hover:text-indigo/80 font-semibold flex items-center gap-2"
                                                >
                                                    View all products
                                                    <ChevronDown className="w-4 h-4 -rotate-90" />
                                                </Link>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {isPublicPage ? (
                                <>
                                    <Link to="/services" className="text-body hover:text-primary transition">Services</Link>
                                    <Link to="/pricing" className="text-body hover:text-primary transition">Pricing</Link>
                                    <Link to="/deploy" className="text-body hover:text-primary transition">Guides</Link>
                                    <Link to="/careers" className="text-body hover:text-primary transition">Careers</Link>
                                    <Link to="/docs" className="flex items-center gap-2 text-body hover:text-primary transition">
                                        <BookOpen className="w-4 h-4" /> Docs
                                    </Link>
                                    <div className="w-px h-6 bg-inner mx-2"></div>
                                    <Link to="/login" className="text-primary font-bold hover:text-indigo transition">Login</Link>
                                    <Link to="/register" className="bg-gradient-primary text-primary px-5 py-2.5 rounded-full hover-shadow-primary transition font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-indigo">
                                        Deploy on Replov
                                    </Link>
                                </>
                            ) : (
                                <>
                                    {!isMarketplace && (
                                        <>
                                            {/* + New Dropdown */}
                                            <NewServiceDropdown
                                                onSelectType={(type) => handleNavigate(`/dashboard?create=${type}`)}
                                                onCreateProject={() => {
                                                    if (!isDashboard) navigate('/dashboard');
                                                    window.dispatchEvent(new CustomEvent('deployhub:newProject'));
                                                }}
                                                onShowInfo={(type) => {
                                                    window.dispatchEvent(new CustomEvent('deployhub:serviceInfo', { detail: { type } }));
                                                }}
                                            />

                                            <div className="w-px h-6 bg-inner mx-1"></div>

                                            <NotificationCenter />
                                        </>
                                    )}

                                    {isMarketplace && (
                                        <>
                                            <button onClick={() => handleNavigate('/dashboard')} className="text-body hover:text-primary transition font-bold px-4">Dashboard</button>
                                            <div className="w-px h-6 bg-inner mx-1"></div>
                                        </>
                                    )}

                                    {!isMarketplace && (
                                        <>
                                            <button
                                                onClick={() => handleNavigate('/networks')}
                                                className="p-2.5 text-muted hover:text-primary hover:bg-hover rounded-xl transition"
                                                title="Networks"
                                            >
                                                <Network className="w-5 h-5" />
                                            </button>
                                        </>
                                    )}

                                    <button
                                        onClick={() => handleNavigate('/profile')}
                                        className="p-2.5 text-muted hover:text-primary hover:bg-hover rounded-xl transition"
                                        title="Profile"
                                    >
                                        <UserIcon className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={logout}
                                        className="flex items-center gap-2 text-red-400 bg-red-400/10 hover:bg-red-400/20 px-4 py-2 rounded-xl transition text-sm font-bold border border-red-500/20"
                                    >
                                        <LogOut className="w-4 h-4" /> Logout
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            aria-label={mobileMenuOpen ? "Close navigation" : "Open navigation"}
                            aria-expanded={mobileMenuOpen}
                            aria-controls="mobile-navigation"
                            className="md:hidden p-2 text-primary bg-input rounded-xl border border-inner"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>

                    {/* Mobile Menu Dropdown */}
                    {mobileMenuOpen && (
                        <nav id="mobile-navigation" aria-label="Mobile" className="md:hidden py-4 border-t border-inner bg-card absolute left-0 right-0 px-4 shadow-xl pb-6 rounded-b-xl border-b -mt-1 z-40 max-h-[80vh] overflow-y-auto">
                            <div className="flex flex-col gap-4 font-medium">
                                {isPublicPage ? (
                                    <>
                                        {/* Products Section */}
                                        <div className="space-y-2">
                                            <div className="text-xs font-bold text-muted uppercase tracking-wider px-2 mb-2">
                                                Products
                                            </div>
                                            
                                            {/* Deployment */}
                                            <div className="text-xs font-semibold text-body px-2 mt-3 mb-1">Deployment</div>
                                            {products.deployment.slice(0, 3).map((product) => (
                                                <Link
                                                    key={product.name}
                                                    to={product.path}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                    className="w-full text-left py-2 px-2 rounded-lg hover:bg-hover flex items-center gap-3"
                                                >
                                                    <product.icon className="w-4 h-4 text-indigo" />
                                                    <span className="text-sm text-body">{product.name}</span>
                                                </Link>
                                            ))}
                                            
                                            {/* Data & Backend */}
                                            <div className="text-xs font-semibold text-body px-2 mt-3 mb-1">Data & Backend</div>
                                            {products.ai.map((product) => (
                                                <a
                                                    key={product.name}
                                                    href={product.path}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => setMobileMenuOpen(false)}
                                                    className="w-full text-left py-2 px-2 rounded-lg hover:bg-hover flex items-center gap-3"
                                                >
                                                    <product.icon className="w-4 h-4 text-emerald-400" />
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-body">{product.name}</span>
                                                        {product.badge && (
                                                            <span className="bg-emerald-400/20 text-emerald-400 text-[10px] font-bold px-1.5 py-0.5 rounded">
                                                                {product.badge}
                                                            </span>
                                                        )}
                                                    </div>
                                                </a>
                                            ))}
                                            
                                            <Link
                                                to="/services"
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="text-sm text-indigo hover:text-indigo/80 font-semibold px-2 py-2 flex items-center gap-2"
                                            >
                                                View all products
                                                <ChevronDown className="w-4 h-4 -rotate-90" />
                                            </Link>
                                        </div>

                                        <hr className="my-2 border-inner" />
                                        <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="text-left py-2 text-body hover:text-primary px-2 rounded-lg hover:bg-hover">Pricing</Link>
                                        <Link to="/deploy" onClick={() => setMobileMenuOpen(false)} className="text-left py-2 text-body hover:text-primary px-2 rounded-lg hover:bg-hover">Guides</Link>
                                        <Link to="/careers" onClick={() => setMobileMenuOpen(false)} className="text-left py-2 text-body hover:text-primary px-2 rounded-lg hover:bg-hover">Careers</Link>
                                        <Link to="/docs" onClick={() => setMobileMenuOpen(false)} className="text-left py-2 text-body hover:text-primary flex items-center gap-2 px-2 rounded-lg hover:bg-hover">
                                            <BookOpen className="w-4 h-4" /> Docs
                                        </Link>
                                        <hr className="my-2 border-inner" />
                                        <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="text-left py-2 text-indigo font-bold px-2 rounded-lg hover:bg-input">Login</Link>
                                        <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="bg-gradient-primary text-primary px-4 py-3 rounded-full text-center mt-2 shadow-lg w-full">
                                            Deploy on Replov
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        {/* + New Button for Mobile */}
                                        {!isMarketplace && (
                                            <div className="mb-4">
                                                <MobileNewServiceMenu
                                                    onSelectType={(type) => handleNavigate(`/dashboard?create=${type}`)}
                                                    onCreateProject={() => {
                                                        if (!isDashboard) navigate('/dashboard');
                                                        window.dispatchEvent(new CustomEvent('deployhub:newProject'));
                                                        setMobileMenuOpen(false);
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {/* Products Section for Logged-in Users */}
                                        <div className="space-y-2 mb-4">
                                            <div className="text-xs font-bold text-muted uppercase tracking-wider px-2 mb-2">
                                                Products
                                            </div>
                                            
                                            {/* Deployment */}
                                            <div className="text-xs font-semibold text-body px-2 mt-3 mb-1">Deployment</div>
                                            {products.deployment.slice(0, 3).map((product, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => handleNavigate(product.path)}
                                                    className="w-full text-left py-2 px-2 rounded-lg hover:bg-hover flex items-center gap-3"
                                                >
                                                    <product.icon className="w-4 h-4 text-indigo" />
                                                    <span className="text-sm text-body">{product.name}</span>
                                                </button>
                                            ))}
                                            
                                            {/* Data & Backend */}
                                            <div className="text-xs font-semibold text-body px-2 mt-3 mb-1">Data & Backend</div>
                                            {products.ai.map((product, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => {
                                                        handleNavigate(product.path, product.external);
                                                    }}
                                                    className="w-full text-left py-2 px-2 rounded-lg hover:bg-hover flex items-center gap-3"
                                                >
                                                    <product.icon className="w-4 h-4 text-emerald-400" />
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-body">{product.name}</span>
                                                        {product.badge && (
                                                            <span className="bg-emerald-400/20 text-emerald-400 text-[10px] font-bold px-1.5 py-0.5 rounded">
                                                                {product.badge}
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>

                                        <hr className="my-2 border-inner" />

                                        {isMarketplace ? (
                                            <button onClick={() => handleNavigate('/dashboard')} className="text-left py-2 text-indigo font-bold px-2 rounded-lg hover:bg-hover">Back to Dashboard</button>
                                        ) : (
                                            <>
                                                <button onClick={() => handleNavigate('/dashboard')} className="text-left py-2 text-primary font-bold px-2 rounded-lg hover:bg-hover">Dashboard</button>
                                                <button onClick={() => handleNavigate('/wallet')} className="text-left py-2 text-primary flex items-center gap-2 px-2 hover:bg-hover rounded-lg">
                                                    <Wallet className="w-4 h-4" /> Wallet
                                                </button>
                                                <button onClick={() => handleNavigate('/networks')} className="text-left py-2 text-primary flex items-center gap-2 px-2 hover:bg-hover rounded-lg">
                                                    <Network className="w-4 h-4" /> Networks
                                                </button>
                                            </>
                                        )}
                                        <button onClick={() => handleNavigate('/profile')} className="text-left py-2 text-primary flex items-center gap-2 px-2 hover:bg-hover rounded-lg">
                                            <UserIcon className="w-4 h-4" /> Profile
                                        </button>
                                        <button onClick={() => handleNavigate('/docs')} className="text-left py-2 text-primary flex items-center gap-2 px-2 hover:bg-hover rounded-lg">
                                            <BookOpen className="w-4 h-4" /> Docs
                                        </button>
                                        <hr className="my-2 border-inner" />
                                        {!isMarketplace && (
                                            <div className="py-2 text-sm text-muted font-normal px-2">Team: <span className="text-body font-medium">{selectedTeam?.name || 'Personal Workspace'}</span></div>
                                        )}
                                        <button onClick={logout} className="text-left py-3 text-red-400 font-bold flex items-center gap-2 px-2 hover:bg-red-400/10 rounded-lg transition mt-2 border border-transparent hover:border-red-400/20">
                                            <LogOut className="w-4 h-4" /> Logout
                                        </button>
                                    </>
                                )}
                            </div>
                        </nav>
                    )}
                </div>
            </nav>

            {/* Modals */}
            {showTeamSettings && (
                <TeamSettingsModal onClose={() => setShowTeamSettings(false)} />
            )}
        </>
    );
};

// Mobile New Service Menu Component
const MobileNewServiceMenu = ({ onSelectType, onCreateProject }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="border border-inner rounded-xl overflow-hidden bg-card">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between px-4 py-3 bg-indigo text-white font-bold hover:bg-indigo/90 transition"
            >
                <div className="flex items-center gap-2">
                    <PlusCircle className="w-5 h-5" />
                    <span>New Service</span>
                </div>
                <ChevronDown className={`w-5 h-5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>

            {expanded && (
                <div className="border-t border-inner">
                    {CORE_SERVICE_TYPES.map((type) => {
                        const Icon = type.icon;
                        return (
                            <button
                                key={type.id}
                                onClick={() => {
                                    onSelectType(type.id);
                                    setExpanded(false);
                                }}
                                className="w-full flex items-start gap-3 px-4 py-3 hover:bg-hover transition text-left border-b border-inner last:border-b-0"
                            >
                                <div className={`p-1.5 rounded-lg ${type.bg} mt-0.5 flex-shrink-0`}>
                                    <Icon className={`w-4 h-4 ${type.color}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-primary">{type.label}</span>
                                        {type.includedWithPlan && (
                                            <span className="text-[10px] font-bold text-green bg-green/10 border border-green/20 px-1.5 py-0.5 rounded-full">INCLUDED</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted mt-0.5 leading-relaxed">{type.menuDescription}</p>
                                </div>
                            </button>
                        );
                    })}
                    <button
                        onClick={() => {
                            onCreateProject();
                            setExpanded(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-hover transition text-left"
                    >
                        <div className="p-1.5 rounded-lg bg-root border border-inner flex-shrink-0">
                            <GitBranch className="w-4 h-4 text-muted" />
                        </div>
                        <div>
                            <span className="text-sm font-bold text-primary">Project</span>
                            <p className="text-xs text-muted mt-0.5">Connect a GitHub repository</p>
                        </div>
                    </button>
                </div>
            )}
        </div>
    );
};

export default Navbar;
